import React from 'react';
import { connect } from 'react-redux';
import { ValueCache } from '@leverege/value-cache';
import { Selector, Text, Popup } from '@leverege/ui-elements';
import { Attributes, DataSources, EditorUtil } from '@leverege/ui-attributes';
import { Config } from '@leverege/plugin';
import I18N from '@leverege/i18n';
import { ItemDelegate } from '@leverege/molten-core';
import LoadingView from "../../../views/LoadingView.js";
import ParentUtil from "./ParentUtil.js";
import S from './ParentSelector.css';

// NOTE: MIGHT WANT TO ADD A ParentRelationshipFormat option to pick
// the attribute to show and what the unset value is
/**
 * A Setter Editor for a ParentRelationship
 */
import { jsx as _jsx } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.imagine.attributes.ParentSelector');
class ParentSelector extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      nextToken: null
    };
    this.cache = new ValueCache();
    this.debounce = null;
    const {
      parentAction,
      attribute
    } = props;
    this.parentNameAttribute = Attributes.getAttribute(attribute?.blueprint?.attribute?.metadata?.setter?.labelAttribute || 'name', parentAction.relationship.objectType);
  }
  componentDidMount() {
    const {
      dispatch,
      parentAction,
      value,
      parentItems
    } = this.props;
    if (value != null) {
      dispatch(parentAction.item(value).get({
        ttl: 300000
      }));
    }
    if (!parentItems) {
      this.loadData();
    }
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      search
    } = this.state;
    if (search !== prevState.search) {
      this.setState({
        nextToken: null
      });
      this.loadData();
    }
  }
  loadData = (loadNextPage = false) => {
    const {
      dispatch,
      parentAction,
      parentItems
    } = this.props;
    const {
      search,
      nextToken
    } = this.state;
    const limit = Config.get('ParentSetter', 'listLimit', 100);
    let offset;
    if (loadNextPage && !nextToken) {
      offset = parentItems?.filteredItems?.findIndex(i => i == null);
      if (offset < 0) {
        offset = 0;
      }
    }
    const sort = {
      field: this.parentNameAttribute.blueprint.field,
      missing: '_first',
      order: 'asc'
    };
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => {
      if (search && search.length) {
        // try adding a sort to query based on name
        dispatch(parentAction.search({
          filter: {
            type: 'groupedText',
            fields: [{
              field: this.parentNameAttribute.blueprint.field,
              type: 'interfixFinalToken'
            }],
            value: search
          },
          sort,
          // update to be the same query as the table filter
          limit,
          offset,
          nextToken
        })).then(({
          nextToken
        }) => {
          this.setState({
            nextToken
          });
        });
      } else {
        dispatch(parentAction.search({
          sort,
          limit,
          offset,
          nextToken
        })).then(({
          nextToken
        }) => {
          this.setState({
            nextToken
          });
        });
      }
    }, 500);
  };
  onBottom = () => {
    if (this.hasMore()) {
      this.loadData(true);
    }
  };
  onChange = event => {
    const {
      onChange,
      parentAction,
      value,
      attribute,
      context
    } = this.props;
    if (event.value === 'loading') {
      // this is a placeholder element, do nothing
      return;
    }
    if (event.value == null) {
      // This is a removal. We dont have to copy data from parent
      if (value != null) {
        const parentRef = parentAction.itemRef(value);
        const parentKey = attribute?.blueprint?.attribute?.path;
        onChange({
          ...event,
          value: {
            parentRef,
            type: 'remove',
            parentKey
          }
        });
      }
      return;
    }
    const parentRef = parentAction.itemRef(event.value);
    const extra = ParentUtil.makeCopyEvents(parentRef, attribute, context);
    const pActions = DataSources.getActions(parentRef);
    const gActions = pActions.child(attribute.source.attribute.parentPath);
    const change = {
      data: {
        [event.data]: event.data
      },
      value: {}
    };
    if (gActions.relationship.isOneToOne) {
      change.value[event.data] = {
        parentRef,
        type: 'set'
      };
    } else if (gActions.relationship.isOneToMany) {
      change.value[event.data] = {
        parentRef,
        type: 'add'
      };
    }

    // On change of the parent, see if there are any other changes desired
    if (extra) {
      extra.forEach((e, idx) => {
        change.data[e.data] = e.data;
        change.value[e.data] = e.value;
      });
    }
    onChange(change);
  };
  onChangePopup = event => {
    const {
      isOpened
    } = event;
    const {
      parentItems
    } = this.props;
    if (!isOpened) {
      this.setState({
        search: ''
      });
    } else if (isOpened && !parentItems) {
      this.loadData();
    }
  };
  onChangeFilter = event => {
    const {
      value
    } = event;
    this.setState({
      search: value
    });
  };
  itemRenderer = (val, name, checked) => {
    if (val.value === 'loading') {
      return /*#__PURE__*/_jsx(Popup.Item, {
        value: val.value,
        className: S.loading,
        children: /*#__PURE__*/_jsx(Text, {
          icon: "fas fa-circle-notch fa-spin",
          children: name
        })
      });
    }
    return name;
  };
  hasMore() {
    const {
      nextToken
    } = this.state;
    const {
      parentItems
    } = this.props;
    return nextToken || parentItems?.filteredItems?.length > 0 && parentItems?.filteredItems?.at(-1) == null;
  }
  render() {
    const {
      allowed,
      editable,
      eventData,
      item,
      loading,
      parentAction,
      parentItems,
      showFilter,
      textVariant,
      title,
      usage,
      value: pValue,
      variant,
      style,
      raw
    } = this.props;
    const {
      nextToken
    } = this.state;
    // Accounting for case where this editor is part of a form using a transaction which is not instantly applied
    const value = pValue?.type === 'remove' ? null : pValue?.parentRef?.id || pValue;
    // const { loadedItem } = this.state
    const itemLoadDone = value && !parentAction.item(value).isLoading();
    const {
      filteredItems
    } = parentItems || {};
    const items = filteredItems?.filter(i => !!i) || [];
    // We are loading if:
    // 1) we have a value and we have not loaded the item for it yet
    // 2) we have not loaded our list yet
    // 3) we dont have items and we are loading
    if (value && !itemLoadDone && item == null || !showFilter && (items == null || items.length === 0) && loading) {
      return /*#__PURE__*/_jsx(LoadingView, {
        inline: true
      });
    }
    if (!showFilter && (items == null || items.length === 0)) {
      return /*#__PURE__*/_jsx(Text, {
        variant: textVariant,
        children: t('noOptions')
      });
    }
    const values = this.cache.calc(items => {
      let hasValue = value == null;
      const r = items == null ? [] : items.map(item => {
        hasValue = hasValue || item.id === value;
        return {
          name: this.parentNameAttribute.get(item) || item.name || item.id,
          value: item.id
        };
      });
      if (!hasValue) {
        r.unshift({
          name: this.parentNameAttribute.get(item) || value,
          value
        });
      }
      r.unshift({
        name: t('notSet'),
        value: null
      });
      if (this.hasMore()) {
        r.push({
          name: t('loading', 'Loading more'),
          value: 'loading'
        });
      }
      return r;
    }, items, item, value, nextToken);
    const vnt = textVariant || (usage === 'table' ? 'columnValue' : `${usage}Value`);
    // add a loading more element to the bottom
    if (!editable || !allowed) {
      const c = values.find(item => item.value === value);
      const label = c ? c.name : value;
      if (raw) {
        return label;
      }

      // TODO: CHANGE TO GET VARIANT
      return /*#__PURE__*/_jsx(Text, {
        variant: vnt,
        children: label
      });
    }
    return /*#__PURE__*/_jsx(Selector, {
      variant: variant,
      eventData: eventData,
      title: title || getTitle,
      stopPropagation: true,
      toggleVariant: vnt ? `${EditorUtil.getVariant(usage, 'parent', 'editor')}|${vnt}` : EditorUtil.getVariant(usage, 'parent', 'editor'),
      toggleAttributes: {
        style: {
          width: '100%',
          ...style
        }
      },
      style: {
        minWidth: '80px',
        width: '100%',
        ...style
      },
      onChange: this.onChange,
      onChangePopup: this.onChangePopup,
      onChangeFilter: this.onChangeFilter,
      renderer: this.itemRenderer,
      values: values,
      value: value,
      onBottom: this.onBottom // update to get next page of data
      ,
      showFilter: showFilter
    });
  }
}
function getTitle(cValue, values) {
  if (cValue.value == null) {
    return '';
  }
  return cValue.name;
}
export default connect((state, props) => {
  const {
    parentAction,
    value
  } = props;
  return {
    item: ItemDelegate.getItem(value),
    parentItems: parentAction.getList(state),
    loading: parentAction.isLoading(state),
    currentLoading: parentAction.isLoading(state),
    allowed: parentAction.canList(state, true)
  };
})(ParentSelector);